<div class="main-view space-y-2 md:space-y-4">
    <app-page-surface-header title="Manage Agent" titleIcon="cpu-bolt" [subtitle]="manageAgentTab === 'deleted'
            ? 'Soft-deleted agents — restore or permanently remove.'
            : 'Published and draft agents - filter, export, and manage training.'" [tabs]="agentSurfaceTabs"
        [activeTabKey]="manageAgentTab" tablistLabel="Agent list"
        (activeTabKeyChange)="onSurfaceTabKeyChange($event)" />

    <app-list-module-surface [config]="listSurfaceConfig" [filters]="listFilters"
        [deletedView]="manageAgentTab === 'deleted'" [errorMessage]="listError" [loading]="loading" [meta]="meta"
        [rowCount]="agents.length" (filtersChange)="onListFiltersChange($event)" (pageChange)="onListPageChange($event)">
        <!-- Projected into the toolbar's own filter row, so it lines up with search, status and
             date range instead of sitting in a bar of its own above them. -->
        <ng-container toolbarFiltersExtra>
            <div class="relative">
                <label class="sr-only" for="agentClientFilter">Client</label>
                <select id="agentClientFilter" name="agentClientFilter"
                    class="form-select w-52 shrink-0 rounded-full border-white-light bg-white py-3 px-4 text-white-dark dark:border-[#191e3a] dark:bg-dark"
                    [ngModel]="clientFilterId" (ngModelChange)="clientFilterId = $event; onClientFilterChange()">
                    <option [ngValue]="null">All clients</option>
                    <option *ngFor="let c of clients" [ngValue]="c.id">{{ clientLabel(c) }}</option>
                </select>
            </div>
        </ng-container>

        <ng-container toolbarActions>
            @if (P.AGENT_EXPORT | hasPermission) {
            <button type="button"
                class="btn rounded-xl border border-gray-200 bg-white p-3 text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
                aria-label="Download">
                <icon-download class="size-5" /> &nbsp;&nbsp;
                <span> Export CSV</span>
            </button>
            }
            @if (P.AGENT_ADD | hasPermission) {
            <button type="button"
                class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
                (click)="navigateToCreateAgent()">
                <icon-plus-circle class="size-5" />
                <span>Add Agent</span>
            </button>
            }
        </ng-container>

        <app-manage-agent-table listTable [rows]="agents" [loading]="loading" [emptyMessage]="emptyMessage"
            [openActionMenuKey]="openActionMenuKey" [menuKeyPrefix]="manageAgentTab" [serialOffset]="serialOffset"
            (toggleMenu)="toggleActionMenu($event.key, $event.event)" (closeMenu)="closeActionMenu()"
            (viewDetail)="onViewDetails($event)" (retrain)="onRetrain($event)"
            (assign)="onAssignToTenant($event)" (version)="onCreateVersion($event)" (pauseAgent)="onPause($event)"
            (delete)="onDelete($event)" (restore)="menuRestore($event)"
            (permanentDelete)="confirmPermanentDelete($event)" />
    </app-list-module-surface>
</div>

<!-- ── Clone Agent modal (2-step) ─────────────────────────────────────────── -->
<dialog *ngIf="assignModalOpen" open
    class="fixed inset-0 z-[200] m-0 flex h-full max-h-none w-full max-w-none items-center justify-center border-0 bg-black/50 p-4 backdrop-blur-[2px]"
    aria-labelledby="assign-modal-title" (click)="closeAssignModal()" (keydown.escape)="closeAssignModal()">
    <div class="relative w-full max-w-lg rounded-[1.75rem] bg-white shadow-xl dark:bg-[#0e1726] dark:shadow-2xl"
        (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">

        <!-- ── Header ─────────────────────────────────────────────── -->
        <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-6 py-4 dark:border-[#191e3a]">
            <div class="flex items-center gap-2.5">
                <icon-user-plus class="size-5 shrink-0 text-gray-600 dark:text-gray-400" />
                <div>
                    <h2 id="assign-modal-title" class="text-base font-bold text-gray-900 dark:text-white">
                        Clone Agent for Another Client
                    </h2>
                    <p *ngIf="!assignSuccessCode" class="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                        Step {{ assignStep }} of 2 &mdash;
                        {{ assignStep === 1 ? 'Select target client' : 'Customise the clone' }}
                    </p>
                </div>
            </div>
            <button type="button"
                class="flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-[#191e3a] dark:text-gray-400 dark:hover:bg-white/5"
                (click)="closeAssignModal()" aria-label="Close">
                <icon-x class="size-4" />
            </button>
        </div>

        <div class="px-6 py-5 space-y-4">

            <!-- ── Source agent pill ──────────────────────────────── -->
            <div *ngIf="assignSourceAgent"
                class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-[#191e3a] dark:bg-white/5">
                <div class="text-xs text-gray-500 dark:text-gray-400">Source agent</div>
                <div
                    class="mt-0.5 flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <span>{{ assignSourceAgent.agent_code ?? assignSourceAgent.uuid }}</span>
                    <span
                        class="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-white/10 dark:text-gray-300">
                        {{ assignSourceAgent.product?.name ?? '&mdash;' }}
                    </span>
                    <span *ngIf="assignSourceAgent.customer_type"
                        class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-white/5 dark:text-gray-400">
                        {{ assignSourceAgent.customer_type }}
                    </span>
                </div>
            </div>

            <!-- ════════════════════════════════════════════════════════
                 SUCCESS STATE
            ════════════════════════════════════════════════════════ -->
            <ng-container *ngIf="assignSuccessCode">
                <div
                    class="flex flex-col items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-8 text-center dark:border-emerald-900/50 dark:bg-emerald-950/30">
                    <icon-circle-check class="size-12 text-emerald-500" />
                    <div>
                        <p class="text-base font-bold text-emerald-800 dark:text-emerald-300">Agent Cloned Successfully!
                        </p>
                        <p class="mt-1 text-sm text-emerald-700 dark:text-emerald-400">
                            A ready-to-use copy has been deployed as <strong>{{ assignSuccessCode }}</strong> for the
                            selected client.
                        </p>
                    </div>
                    <button type="button"
                        class="btn rounded-full bg-[#303030] px-8 py-2.5 text-sm text-white hover:bg-black"
                        (click)="closeAssignModal()">
                        Done
                    </button>
                </div>
            </ng-container>

            <!-- ════════════════════════════════════════════════════════
                 STEP 1 — SELECT CLIENT
            ════════════════════════════════════════════════════════ -->
            <ng-container *ngIf="!assignSuccessCode && assignStep === 1">

                <p *ngIf="assignError"
                    class="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-100"
                    role="alert">{{ assignError }}</p>

                <!-- Search -->
                <div>
                    <label for="assignTenantSearch"
                        class="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Select Client <span class="text-red-500">*</span>
                    </label>
                    <input id="assignTenantSearch" type="text"
                        class="form-input w-full rounded-xl border-gray-200 dark:border-[#191e3a] dark:bg-[#0e1726]"
                        placeholder="Search by name, subdomain or email&hellip;" [(ngModel)]="assignTenantSearch"
                        name="assignTenantSearch" />
                </div>

                <!-- Client list -->
                <div class="max-h-52 overflow-y-auto rounded-xl border border-gray-200 dark:border-[#191e3a]">
                    <p *ngIf="assignTenantsLoading" class="py-6 text-center text-sm text-gray-400">Loading
                        clients&hellip;</p>
                    <p *ngIf="!assignTenantsLoading && assignTenants.length === 0"
                        class="py-6 text-center text-sm text-gray-400">
                        No clients have this product assigned.
                    </p>
                    <p *ngIf="!assignTenantsLoading && assignTenants.length > 0 && assignFilteredTenants.length === 0"
                        class="py-6 text-center text-sm text-gray-400">
                        No clients found.
                    </p>

                    <ng-container *ngFor="let t of assignFilteredTenants">
                        <!-- Current client — disabled, shown at bottom -->
                        <div *ngIf="t.id === assignSourceAgent?.tenant_id"
                            class="flex w-full items-center gap-3 px-4 py-3 opacity-40 cursor-not-allowed select-none">
                            <span
                                class="flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-600 dark:bg-white/10 dark:text-gray-300">
                                {{ (t.tenant_name || '?').charAt(0).toUpperCase() }}
                            </span>
                            <div class="min-w-0 flex-1">
                                <div class="truncate font-medium text-gray-900 dark:text-white">{{ t.tenant_name ??
                                    '&mdash;' }}</div>
                                <div class="truncate text-xs text-gray-500 dark:text-gray-400">
                                    {{ t.tenant_subdomain ?? t.email ?? '&mdash;' }} &middot; current client
                                </div>
                            </div>
                        </div>
                        <!-- Selectable client -->
                        <button *ngIf="t.id !== assignSourceAgent?.tenant_id" type="button"
                            class="flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition hover:bg-gray-50 dark:hover:bg-white/5"
                            [ngClass]="assignSelectedTenantId === t.id ? 'bg-[#FFD85F]/20 dark:bg-yellow-950/30' : ''"
                            (click)="assignSelectedTenantId = t.id">
                            <span
                                class="flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                                [ngClass]="assignSelectedTenantId === t.id ? 'bg-[#303030] text-white' : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300'">
                                {{ (t.tenant_name || '?').charAt(0).toUpperCase() }}
                            </span>
                            <div class="min-w-0 flex-1">
                                <div class="truncate font-medium text-gray-900 dark:text-white">{{ t.tenant_name ??
                                    '&mdash;' }}</div>
                                <div class="truncate text-xs text-gray-500 dark:text-gray-400">{{ t.tenant_subdomain ??
                                    t.email ?? '&mdash;' }}</div>
                            </div>
                            <icon-circle-check *ngIf="assignSelectedTenantId === t.id"
                                class="size-4 shrink-0 text-emerald-500" />
                        </button>
                    </ng-container>
                </div>

                <!-- Actions -->
                <div class="flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4 dark:border-[#191e3a]">
                    <button type="button"
                        class="btn rounded-full border border-gray-300 bg-white px-6 py-2.5 text-sm dark:border-[#191e3a] dark:bg-[#0e1726]"
                        (click)="closeAssignModal()">
                        Cancel
                    </button>
                    <button type="button"
                        class="btn inline-flex items-center gap-2 rounded-full bg-[#303030] px-6 py-2.5 text-sm text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
                        [disabled]="!assignSelectedTenantId" (click)="assignGoToStep2()">
                        Next &rarr;
                    </button>
                </div>
            </ng-container>

            <!-- ════════════════════════════════════════════════════════
                 STEP 2 — CUSTOMISE THE CLONE
            ════════════════════════════════════════════════════════ -->
            <ng-container *ngIf="!assignSuccessCode && assignStep === 2">

                <p *ngIf="assignError"
                    class="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-100"
                    role="alert">{{ assignError }}</p>

                <!-- ── Agent name ──────────────────────────────────── -->
                <div>
                    <label for="cloneAgentName"
                        class="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Agent Name <span class="text-red-500">*</span>
                    </label>
                    <input id="cloneAgentName" type="text" class="form-input w-full rounded-xl"
                        [class.border-danger]="cloneNameError" placeholder="Enter name for the cloned agent"
                        [(ngModel)]="cloneName" name="cloneAgentName" autocomplete="off" />
                    <p *ngIf="cloneNameError" class="mt-1 text-xs text-danger" role="alert">
                        Agent name is required.
                    </p>
                </div>

                <!-- ── Customer Type (PD products only) ───────────── -->
                <ng-container *ngIf="assignSourceIsPd">
                    <div>
                        <span class="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Customer Type
                        </span>
                        <div class="flex flex-wrap gap-2">
                            <button type="button" class="rounded-xl border px-4 py-2 text-sm font-medium transition"
                                [ngClass]="cloneCustomerType === 'salaried'
                                    ? 'border-[#FFD85F] bg-[#FFD85F] text-black'
                                    : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726]'"
                                (click)="cloneCustomerType = 'salaried'">
                                Salaried
                            </button>
                            <button type="button" class="rounded-xl border px-4 py-2 text-sm font-medium transition"
                                [ngClass]="cloneCustomerType === 'self-employed'
                                    ? 'border-[#FFD85F] bg-[#FFD85F] text-black'
                                    : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726]'"
                                (click)="cloneCustomerType = 'self-employed'">
                                Self Employed
                            </button>
                            <button type="button" class="rounded-xl border px-4 py-2 text-sm font-medium transition"
                                [ngClass]="cloneCustomerType === 'self-employed-np'
                                    ? 'border-[#FFD85F] bg-[#FFD85F] text-black'
                                    : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726]'"
                                (click)="cloneCustomerType = 'self-employed-np'">
                                Self-Employed (NP)
                            </button>
                        </div>
                    </div>

                    <!-- ── Customer Sub-Profile ──────────────────────── -->
                    <div>
                        <span class="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Customer Sub-Profile
                        </span>
                        <div class="flex flex-wrap gap-2">
                            <button type="button" class="rounded-xl border px-4 py-2 text-sm font-medium transition"
                                [ngClass]="cloneSubProfile === 'bank'
                                    ? 'border-[#FFD85F] bg-[#FFD85F] text-black'
                                    : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726]'"
                                (click)="cloneSubProfile = 'bank'">
                                Bank
                            </button>
                            <button type="button" class="rounded-xl border px-4 py-2 text-sm font-medium transition"
                                [ngClass]="cloneSubProfile === 'cash'
                                    ? 'border-[#FFD85F] bg-[#FFD85F] text-black'
                                    : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726]'"
                                (click)="cloneSubProfile = 'cash'">
                                Cash
                            </button>
                        </div>
                    </div>
                </ng-container>

                <!-- ── What to copy ───────────────────────────────── -->
                <div>
                    <span class="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                        What to copy from source
                    </span>
                    <div class="space-y-2.5 rounded-xl border border-gray-200 p-3 dark:border-[#191e3a]">

                        <label class="flex cursor-pointer items-start gap-3">
                            <input type="checkbox" class="form-checkbox mt-0.5 rounded" [(ngModel)]="cloneCopyQuestions"
                                name="cloneCopyQuestions" />
                            <div>
                                <div class="text-sm font-medium text-gray-900 dark:text-white">Training Questions</div>
                                <div class="text-xs text-gray-500 dark:text-gray-400">Interview Q&amp;A by profile
                                    (general, education, employment&hellip;)</div>
                            </div>
                        </label>

                        <label class="flex cursor-pointer items-start gap-3">
                            <input type="checkbox" class="form-checkbox mt-0.5 rounded" [(ngModel)]="cloneCopyDocuments"
                                name="cloneCopyDocuments" />
                            <div>
                                <div class="text-sm font-medium text-gray-900 dark:text-white">Training Documents</div>
                                <div class="text-xs text-gray-500 dark:text-gray-400">Reference document metadata
                                    uploaded during training</div>
                            </div>
                        </label>

                        <label class="flex cursor-pointer items-start gap-3">
                            <input type="checkbox" class="form-checkbox mt-0.5 rounded" [(ngModel)]="cloneCopySimulate"
                                name="cloneCopySimulate" />
                            <div>
                                <div class="text-sm font-medium text-gray-900 dark:text-white">Simulate Documents</div>
                                <div class="text-xs text-gray-500 dark:text-gray-400">PAN, selfie, and loan docs used
                                    during the simulate step</div>
                            </div>
                        </label>

                    </div>
                    <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                        Unchecked sections will be blank in the clone &mdash; fill them via Retrain after creation.
                    </p>
                </div>

                <!-- Actions -->
                <div class="flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4 dark:border-[#191e3a]">
                    <button type="button"
                        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-6 py-2.5 text-sm dark:border-[#191e3a] dark:bg-[#0e1726]"
                        [disabled]="assignLoading" (click)="assignGoToStep1()">
                        &larr; Back
                    </button>
                    <button type="button"
                        class="btn rounded-full bg-[#303030] px-6 py-2.5 text-sm text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
                        [disabled]="assignLoading" (click)="confirmAssign()">
                        {{ assignLoading ? 'Creating clone&hellip;' : 'Clone Agent' }}
                    </button>
                </div>
            </ng-container>

        </div>
    </div>
</dialog>

<app-delete-confirm-modal [open]="showDeleteModal" [heading]="deleteModalHeading" [entityName]="selectedAgentDeleteName"
    entityNoun="agent" [variant]="deleteVariant" [loading]="deleteLoading()" [error]="deleteError"
    (cancelled)="closeDeleteModal()" (confirmed)="submitDelete()" />

<app-restore-confirm-modal [open]="showRestoreModal" heading="Restore agent?" [entityName]="selectedAgent?.name ?? ''"
    activeListLabel="active agent list" [loading]="restoreLoading()" (cancelled)="closeRestoreModal()"
    (confirmed)="submitRestore()" />

<!-- ── New Version modal ──────────────────────────────────────────────────── -->
<dialog *ngIf="versionModalOpen" open
    class="fixed inset-0 z-[200] m-0 flex h-full max-h-none w-full max-w-none items-center justify-center border-0 bg-transparent p-4 !mt-0"
    (keydown.escape)="closeVersionModal()">
    <button type="button" class="absolute inset-0 bg-black/50 backdrop-blur-[1px]" (click)="closeVersionModal()"
        aria-label="Close dialog"></button>
    <div class="relative z-10 w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl dark:bg-[#0e1726]">
        <ng-container *ngIf="!versionResult; else versionDoneTpl">
            <div class="mb-4 flex items-start justify-between">
                <div>
                    <h2 class="text-base font-bold text-black dark:text-white">Publish new version</h2>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {{ versionSourceAgent?.name }} — current
                        <code
                            class="rounded bg-gray-100 px-1 py-0.5 font-mono dark:bg-white/10">v{{ versionSourceAgent?.version }}</code>
                    </p>
                </div>
                <button type="button" class="text-gray-400 hover:text-gray-600" (click)="closeVersionModal()">✕</button>
            </div>

            <span class="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">Change type</span>
            <div class="mb-3 grid grid-cols-3 gap-2">
                <button type="button" class="rounded-xl border px-3 py-2 text-xs"
                    [ngClass]="versionChangeType === 'MAJOR' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 dark:border-white/10'"
                    (click)="versionChangeType = 'MAJOR'">Major</button>
                <button type="button" class="rounded-xl border px-3 py-2 text-xs"
                    [ngClass]="versionChangeType === 'MINOR' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 dark:border-white/10'"
                    (click)="versionChangeType = 'MINOR'">Minor</button>
                <button type="button" class="rounded-xl border px-3 py-2 text-xs"
                    [ngClass]="versionChangeType === 'PATCH' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 dark:border-white/10'"
                    (click)="versionChangeType = 'PATCH'">Patch</button>
            </div>
            <p
                class="mb-3 rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-500 dark:bg-white/5 dark:text-gray-400">
                <ng-container *ngIf="versionChangeType === 'MAJOR'">Major creates a <strong>new agent</strong>
                    (duplicate) linked to this one — both keep running.</ng-container>
                <ng-container *ngIf="versionChangeType !== 'MAJOR'">{{ versionChangeType === 'MINOR' ? 'Minor' : 'Patch'
                    }} updates <strong>this agent in place</strong> and snapshots the config.</ng-container>
            </p>

            <div *ngIf="versionChangeType === 'MAJOR'" class="mb-3">
                <label for="version-major-name"
                    class="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">New agent name
                    (optional)</label>
                <input id="version-major-name" type="text" class="form-input w-full rounded-xl"
                    [(ngModel)]="versionMajorName" name="versionMajorName"
                    [placeholder]="versionSourceAgent?.name ?? ''" autocomplete="off" />
            </div>

            <label for="version-notes" class="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">Change
                notes (optional)</label>
            <textarea id="version-notes" class="form-textarea w-full rounded-xl" rows="2" [(ngModel)]="versionNotes"
                name="versionNotes" placeholder="What changed in this version?"></textarea>

            <p *ngIf="versionError" class="mt-2 text-xs text-danger">{{ versionError }}</p>

            <div class="mt-5 flex items-center justify-between">
                <button type="button" class="text-xs font-medium text-primary underline"
                    (click)="openVersionHistory(versionSourceAgent!)">View history</button>
                <div class="flex gap-2">
                    <button type="button"
                        class="rounded-full border px-5 py-2 text-sm hover:bg-gray-50 dark:hover:bg-white/5"
                        (click)="closeVersionModal()">Cancel</button>
                    <button type="button"
                        class="rounded-full bg-[#303030] px-5 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-50"
                        [disabled]="versionLoading" (click)="submitVersion()">
                        {{ versionLoading ? 'Publishing…' : 'Publish version' }}
                    </button>
                </div>
            </div>
        </ng-container>

        <ng-template #versionDoneTpl>
            <div class="py-2 text-center">
                <h2 class="text-base font-bold text-black dark:text-white">
                    Version {{ versionResult?.version }} published
                </h2>
                <p *ngIf="versionResult?.isMajor" class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Created as a new agent
                    <code
                        class="rounded bg-gray-100 px-1.5 py-0.5 font-mono dark:bg-white/10">{{ versionResult?.agent_code }}</code>
                    —
                    the original stays unchanged.
                </p>
                <p *ngIf="!versionResult?.isMajor" class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    This agent is now at <strong>v{{ versionResult?.version }}</strong>. The previous config was
                    snapshotted.
                </p>
                <button type="button" class="mt-5 w-full rounded-full bg-[#303030] px-6 py-2 text-sm text-white"
                    (click)="closeVersionModal()">Done</button>
            </div>
        </ng-template>
    </div>
</dialog>

<!-- ── Version history modal ──────────────────────────────────────────────── -->
<dialog *ngIf="historyModalOpen" open
    class="fixed inset-0 z-[200] m-0 flex h-full max-h-none w-full max-w-none items-center justify-center border-0 bg-transparent p-4 !mt-0"
    (keydown.escape)="closeVersionHistory()">
    <button type="button" class="absolute inset-0 bg-black/50 backdrop-blur-[1px]" (click)="closeVersionHistory()"
        aria-label="Close dialog"></button>
    <div class="relative z-10 w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl dark:bg-[#0e1726]">
        <div class="mb-4 flex items-start justify-between">
            <h2 class="text-base font-bold text-black dark:text-white">Version history — {{ historySourceAgent?.name }}
            </h2>
            <button type="button" class="text-gray-400 hover:text-gray-600" (click)="closeVersionHistory()">✕</button>
        </div>
        <p *ngIf="historyLoading" class="py-6 text-center text-sm text-gray-400">Loading…</p>
        <p *ngIf="!historyLoading && historyVersions.length === 0" class="py-6 text-center text-sm text-gray-400">No
            versions published yet.</p>
        <ul *ngIf="!historyLoading && historyVersions.length" class="max-h-80 space-y-2 overflow-auto">
            <li *ngFor="let v of historyVersions"
                class="rounded-xl border border-gray-200 px-3 py-2 dark:border-white/10">
                <div class="flex items-center justify-between">
                    <span class="text-sm font-semibold text-black dark:text-white">v{{ v.version }}</span>
                    <span class="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{{
                        v.change_type }}</span>
                </div>
                <p *ngIf="v.notes" class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ v.notes }}</p>
                <p class="mt-1 text-[11px] text-gray-400">{{ v.created_at | date:'dd MMM y, HH:mm' }}</p>
            </li>
        </ul>
    </div>
</dialog>